/**
 * What the Management Portal launch deliberately does NOT include.
 *
 * Decision by Rob, 2026-09-02, after the design-match audit: the surfaces whose
 * controls render but can never work are OUT of launch scope. They are not to
 * ship as dead controls, and they are not to be counted as gaps against the
 * approved design.
 *
 * The audit found 30 such components. They divide into two kinds:
 *
 *  1. A whole screen with no backend at all -- O18 Scenarios, O19 Readiness,
 *     O21 Resets, O22 Evidence make no network call whatsoever; there is no
 *     read route for any of them in lambda/dashboard/demo-env/handler.py.
 *     These lose their routes: an operator cannot reach a screen that can
 *     only ever say "nothing here".
 *
 *  2. A working screen carrying one or more controls that cannot fire --
 *     O01's four command buttons, O04's approve/reject/revoke, O05's schedule
 *     controls, and so on. The screen keeps its read, and the disabled button
 *     is replaced by a plain statement of what is missing.
 *
 * Why a register rather than an edit to the workbook: the Screen Contract
 * sheet is the approved design and stays intact. This records a scope DECISION
 * against it, the same way DECISIONS.D12 records the public-corpus encryption
 * call. Reversing the decision means deleting an entry here, not restoring a
 * document.
 */
import React from 'react';
import { Info } from 'lucide-react';

export interface ScopeDecision {
  /** Screen id from the approved contract. */
  id: string;
  /** Human name, so the register reads without the workbook open. */
  name: string;
  /** Why it cannot work today -- the concrete missing thing, never "TODO". */
  because: string;
  /** 'screen' loses its route; 'controls' keeps the screen, drops the buttons. */
  removes: 'screen' | 'controls';
}

export const OUT_OF_LAUNCH_SCOPE: readonly ScopeDecision[] = [
  {
    id: 'O18',
    name: 'Demo scenarios',
    because: 'no scenario catalogue exists; demo-env exposes only a mutating /scenario-reset POST',
    removes: 'screen',
  },
  {
    id: 'O19',
    name: 'Demo readiness',
    because: 'no readiness verifier service exists to produce a verdict',
    removes: 'screen',
  },
  {
    id: 'O21',
    name: 'Demo resets',
    because: 'no reset-preview read model exists, so a reset could only be executed blind',
    removes: 'screen',
  },
  {
    id: 'O22',
    name: 'Demo evidence',
    because: 'no evidence-chain store exists for demo runs',
    removes: 'screen',
  },
  {
    id: 'O16',
    name: 'Recovery rehearsals',
    because: 'no rehearsal record store exists; start and close would write nowhere',
    removes: 'controls',
  },
  {
    id: 'O17',
    name: 'Recovery evidence',
    because: 'no evidence record store exists; the three state cards are a constant array',
    removes: 'controls',
  },
  {
    id: 'O04',
    name: 'Operations approvals',
    because:
      'no policy_descriptor approval-plan store exists, so the backend returns an empty plan list by construction',
    removes: 'controls',
  },
  {
    id: 'O20',
    name: 'Demo holds',
    because: 'acquiring a hold needs a single-operator approval receipt that is not issued yet',
    removes: 'controls',
  },
  {
    id: 'O01',
    name: 'Operations command centre',
    because: 'the typed-command and governed-preflight paths are not built',
    removes: 'controls',
  },
  {
    id: 'O02',
    name: 'Operations attention',
    because: 'assign, acknowledge and snooze have no writer behind them',
    removes: 'controls',
  },
  {
    id: 'O05',
    name: 'Operations schedules',
    because: 'schedule create, pause and edit have no writer behind them',
    removes: 'controls',
  },
  {
    id: 'O06',
    name: 'Operations incidents',
    because: 'incident declare, add-event and resolve have no writer behind them',
    removes: 'controls',
  },
  {
    id: 'O14',
    name: 'Restore plans',
    because: 'restore execution requires provider preflight and approval that are not built',
    removes: 'controls',
  },
  {
    id: 'R12',
    name: 'Data snapshots',
    because:
      'the catalogue filters on ChangeSet types no part of the UI can produce, so it can never hold a row',
    removes: 'controls',
  },
  {
    id: 'K06',
    name: 'User guides',
    because: 'guides are .docx under documentation/ and the knowledge pack indexes only source files',
    removes: 'controls',
  },
  {
    id: 'K07',
    name: 'Knowledge evidence and pack status',
    because: 'the ChangeSet republish route is not deployed',
    removes: 'controls',
  },
  {
    id: 'P26',
    name: 'Usage & cost by AI role',
    because: 'no per-role meter exists, so every role reads "not tracked" regardless of data',
    removes: 'controls',
  },
];

const BY_ID = new Map(OUT_OF_LAUNCH_SCOPE.map((d) => [d.id, d]));

/** The scope decision for a screen, if it has one. */
export function scopeDecisionFor(screenId: string | undefined): ScopeDecision | undefined {
  return screenId ? BY_ID.get(screenId) : undefined;
}

/** Screen ids whose whole route is withdrawn from the launch. */
export const WITHDRAWN_SCREEN_IDS: readonly string[] = OUT_OF_LAUNCH_SCOPE.filter(
  (d) => d.removes === 'screen',
).map((d) => d.id);

/**
 * Says plainly that something is not in the launch, and why.
 *
 * This replaces a permanently-disabled button. A greyed-out "Approve" invites
 * the operator to wonder what permission they are missing; this tells them
 * there is nothing to be missing yet.
 */
export const NotInLaunchScope: React.FC<{
  /** What the removed control claimed to do, e.g. 'Approve, reject or revoke'. */
  control: string;
  /** The concrete missing thing. */
  because: string;
  className?: string;
}> = ({ control, because, className }) => (
  <p
    role="note"
    data-testid="not-in-launch-scope"
    className={`flex items-start gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600 ${className ?? ''}`}
  >
    <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" aria-hidden="true" />
    <span>
      <span className="font-semibold">{control}</span> is not in the launch — {because}.
    </span>
  </p>
);
